// src/pages/KeywordTool.tsx
import React, { useState } from "react";
import {
  Table,
  Input,
  Button,
  Card,
  Typography,
  Progress,
  Tooltip,
  message,
  Empty,
} from "antd";
import { SearchOutlined, RiseOutlined, CopyOutlined } from "@ant-design/icons";
import { fetchKeywordSuggestions } from "../services/api";

const { Text } = Typography;

interface KeywordData {
  word: string;
  score: number;
  tags?: string[];
}

const KeywordTool: React.FC = () => {
  const [keyword, setKeyword] = useState("");
  const [data, setData] = useState<KeywordData[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!keyword) return message.warning("Please enter a seed keyword");
    setLoading(true);
    try {
      const results = await fetchKeywordSuggestions(keyword);
      // Normalizing score for visualization (Datamuse scores vary wildly)
      const maxScore = Math.max(...results.map((r: any) => r.score));
      const normalized = results.map((r: any) => ({
        ...r,
        percent: Math.min(Math.round((r.score / maxScore) * 100), 100),
      }));
      setData(normalized);
    } catch (err) {
      message.error("Failed to fetch keywords");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success("Copied!");
  };

  const columns = [
    {
      title: "Keyword Opportunity",
      dataIndex: "word",
      key: "word",
      render: (text: string) => (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Text strong style={{ fontSize: 16 }}>
            {text}
          </Text>
          <Tooltip title="Copy">
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              onClick={() => copyToClipboard(text)}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Relevance Score",
      dataIndex: "percent",
      key: "percent",
      sorter: (a: any, b: any) => a.score - b.score,
      width: 250,
      render: (percent: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Progress
            percent={percent}
            showInfo={false}
            strokeColor={{
              "0%": "#108ee9",
              "100%": "#87d068",
            }}
            trailColor="rgba(255,255,255,0.1)"
          />
          <Text type="secondary" style={{ fontSize: 12, minWidth: 30 }}>
            {percent}%
          </Text>
        </div>
      ),
    },
  ];

  return (
    <Card className="glass-card" bordered={false}>
      <div
        style={{
          maxWidth: 600,
          margin: "0 auto 40px auto",
          textAlign: "center",
        }}
      >
        <Typography.Title level={2} className="gradient-text">
          Unlock Search Intent
        </Typography.Title>
        <Typography.Text type="secondary">
          Discover semantic variations and high-value keywords powered by AI.
        </Typography.Text>
        <div style={{ display: "flex", gap: "10px", marginTop: "30px" }}>
          <Input
            size="large"
            placeholder="Enter seed keyword (e.g. 'Reaction Gaming')"
            prefix={<SearchOutlined style={{ color: "#666" }} />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ borderRadius: 8 }}
          />
          <Button
            type="primary"
            size="large"
            icon={<RiseOutlined />}
            onClick={handleSearch}
            loading={loading}
            style={{ borderRadius: 8, padding: "0 30px" }}
          >
            Analyze
          </Button>
        </div>
      </div>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="word"
        loading={loading}
        pagination={{ pageSize: 8 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Enter a keyword to start mining data"
            />
          ),
        }}
      />
    </Card>
  );
};

export default KeywordTool;
